import { useTranslation } from 'react-i18next';
import React, {useEffect, useState} from 'react';
import httpService from "../../../services/http.service";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import UserCard from "../../Shared/User/Card";
import * as queryString from "query-string";
import {USER_TYPE_PAGE} from "../../../utils/index";
import AppPagination from "../../Shared/AppPagination";
import KpiCard from "../../Shared/KpiCard";

/** Champs de recherche acceptés par l'API (un seul à la fois, comme avant). */
const SEARCH_FIELDS = ['user_username', 'user_surname', 'user_name', 'user_email', 'user_id'];
const FILTER_KEYS = [...SEARCH_FIELDS, 'status', 'referral_override', 'user_type'];
const formatInt = (value) => (value === undefined || value === null ? '–' : Number(value).toLocaleString());

/**
 * Utilisateurs et pages (/users, ?user_type=PAGE pour les pages) : carte de statistiques, recherche sur un champ
 * au choix, filtres de statut et de parrainage dans l'URL, cartes utilisateur paginées (deux par ligne).
 */
const UserList = ({history, location}) => {
    const { t } = useTranslation();
    const query = queryString.parse(location.search);
    const isPage = query.user_type === USER_TYPE_PAGE;

    const currentField = SEARCH_FIELDS.find(field => query[field]) || 'user_username';
    const [users, setUsers] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [meta, setMeta] = useState(null);
    const [stats, setStats] = useState(null);
    const [search, setSearch] = useState({field: currentField, value: query[currentField] || ''});

    const setFilters = (changes) => {
        const next = {...query, ...changes};
        if (!('page' in changes)) delete next.page;
        Object.keys(next).forEach(key => (next[key] === '' || next[key] === undefined || next[key] === null) && delete next[key]);
        history.push(`/users?${queryString.stringify(next)}`);
    };

    useEffect(() => {
        setStats(null);
        (async () => {
            try {
                const {data} = await httpService.get('/users/stats', {params: query.user_type ? {user_type: query.user_type} : {}});
                setStats(data?.data || null);
            } catch (e) {
                setStats(null);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.user_type]);

    useEffect(() => {
        const field = SEARCH_FIELDS.find(key => query[key]) || 'user_username';
        setSearch({field, value: query[field] || ''});
        setFetching(true);
        (async () => {
            try {
                const params = {page: query.page || 1};
                FILTER_KEYS.forEach(key => query[key] && (params[key] = query[key]));
                const {data: response} = await httpService.get('/users', {params});
                setUsers(response.data || []);
                setMeta({
                    currentPage: response?.meta?.current_page,
                    lastPage: response?.meta?.last_page,
                    totalItems: response?.meta?.total,
                    hasNext: !!response?.links?.next,
                    hasPrevious: !!response?.links?.prev,
                });
            } catch (e) {
                setUsers([]);
                setMeta(null);
            } finally {
                setFetching(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const onSearch = (e) => {
        e.preventDefault();
        const cleared = Object.fromEntries(SEARCH_FIELDS.map(field => [field, '']));
        setFilters({...cleared, [search.field]: search.value.trim().replace(/^@/, '')});
    };

    const onPaginate = (direction) => {
        const current = parseInt(query.page || 1, 10);
        setFilters({page: direction === 'previous' ? current - 1 : current + 1});
    };

    const hasFilters = [...SEARCH_FIELDS, 'status', 'referral_override'].some(key => query[key]);
    const share = (value) => (stats && stats.total ? Math.round((value / stats.total) * 100) : 0);

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={isPage ? t("Pages") : t("Utilisateurs")} />

            {/* Statistiques */}
            <div className="row clearfix equal-height-row">
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-users" tone="blue" label={isPage ? t("Pages") : t("Inscrits")} loading={!stats}
                             value={formatInt(stats?.total)}
                             hint={t("+{{w}} en 7 jours · +{{m}} en 30 jours", {w: formatInt(stats?.last_7_days), m: formatInt(stats?.last_30_days)})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-check-circle" tone="green" label={t("Certifiés")} loading={!stats}
                             value={formatInt(stats?.verified)} progress={share(stats?.verified)}
                             hint={t("{{p}} % des comptes", {p: share(stats?.verified)})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-star" tone="orange" label={t("Gold")} loading={!stats}
                             value={formatInt(stats?.gold)} progress={share(stats?.gold)}
                             hint={t("{{p}} % des comptes", {p: share(stats?.gold)})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-ban" tone="purple" label={t("Bloqués")} loading={!stats}
                             value={formatInt(stats?.blocked)} hint={t("{{m}} rendu(s) muet(s)", {m: formatInt(stats?.muted)})} />
                </div>
            </div>

            {/* Recherche et filtres */}
            <div className="card">
                <div className="body">
                    <form onSubmit={onSearch} className="row">
                        <div className="col-lg-6 col-md-12 mb-2">
                            <div className="input-group">
                                <div className="input-group-prepend">
                                    <select className="form-control" style={{maxWidth: 190}} value={search.field}
                                            onChange={(e) => setSearch({...search, field: e.target.value})}>
                                        <option value="user_username">{t("Nom d'utilisateur")}</option>
                                        <option value="user_surname">{t("Nom")}</option>
                                        {!isPage && <option value="user_name">{t("Prénom")}</option>}
                                        <option value="user_email">{t("Adresse email")}</option>
                                        <option value="user_id">{t("Identifiant")}</option>
                                    </select>
                                </div>
                                <input className="form-control" placeholder={t("Rechercher…")} value={search.value}
                                       onChange={(e) => setSearch({...search, value: e.target.value})} />
                                <div className="input-group-append">
                                    <button className="btn btn-primary" type="submit"><i className="fa fa-search"></i></button>
                                </div>
                            </div>
                        </div>
                        <div className={`${isPage ? 'col-lg-6' : 'col-lg-3'} col-md-6 mb-2`}>
                            <select className="form-control" value={query.status || ''} onChange={(e) => setFilters({status: e.target.value})}>
                                <option value="">{t("Tous les statuts")}</option>
                                <option value="verified">{t("Certifiés")}</option>
                                <option value="unverified">{t("Non certifiés")}</option>
                                <option value="gold">{t("Gold")}</option>
                                <option value="blocked">{t("Bloqués")}</option>
                                <option value="muted">{t("Rendus muets")}</option>
                                <option value="inactive">{t("Inactifs")}</option>
                            </select>
                        </div>
                        {!isPage && (
                            <div className="col-lg-3 col-md-6 mb-2">
                                <select className="form-control" value={query.referral_override || ''} onChange={(e) => setFilters({referral_override: e.target.value})}>
                                    <option value="">{t("Parrainage : tous les utilisateurs")}</option>
                                    <option value="any">{t("Avec une exception de parrainage")}</option>
                                    <option value="enabled">{t("Parrainage forcé activé")}</option>
                                    <option value="disabled">{t("Parrainage forcé désactivé")}</option>
                                </select>
                            </div>
                        )}
                    </form>
                    <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">{meta ? t("{{n}} résultat(s)", {n: formatInt(meta.totalItems)}) : ''}</small>
                        {hasFilters && (
                            <button type="button" className="btn btn-link btn-sm p-0"
                                    onClick={() => history.push(`/users?${queryString.stringify(query.user_type ? {user_type: query.user_type} : {})}`)}>
                                <i className="fa fa-times"></i>{' '}{t("Effacer les filtres")}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {fetching ? <FecthingSpinner /> : (
                <div className="row clearfix equal-height-row">
                    {users.length === 0 ? (
                        <div className="col-md-12">
                            <div className="card"><div className="body text-center text-muted py-5">{t("Aucun compte ne correspond à la recherche.")}</div></div>
                        </div>
                    ) : users.map(user => (
                        <div className="col-lg-6 col-md-12" key={user?.user_id}>
                            <UserCard user={user}/>
                        </div>
                    ))}
                    {meta && meta.lastPage > 1 && (
                        <div className="col-md-12">
                            <div className="card">
                                <div className="body">
                                    <AppPagination
                                        onNavigate={onPaginate}
                                        currentPage={meta.currentPage}
                                        hasNext={meta.hasNext}
                                        hasPrevious={meta.hasPrevious}
                                        totalItems={meta.totalItems}
                                        lastPage={meta.lastPage}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default UserList
